import { useEffect, useRef, useState } from "react";
import { FontSize } from "config/font";
import { PlaceholderText } from "../dropDownWithLabel/dropDownWithLabel.styled";
import {
  ActionButton,
  ActionButtons,
  Checkbox,
  MoreIndicator,
  MultiSelectArrow,
  MultiSelectContainer,
  MultiSelectInput,
  MultiSelectItem,
  MultiSelectLabel,
  MultiSelectList,
  OptionLabel,
  SelectedContainer,
  SelectedTag,
} from "./multiSelectWithLabel.styled";
import { EyeIcon } from "../textInput/textInput.styled";
import { Eye } from "../textInput/textInput.component";
import { useTranslation } from "react-i18next";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectWithLabelProps {
  label: string;
  options: MultiSelectOption[];
  values?: string[];
  required?: boolean;
  hasError?: boolean;
  fontSize?: FontSize;
  placeholder?: string;
  maxDisplayTags?: number;
  showSelectAll?: boolean;
  disabled?: boolean;
  onChange?: (values: string[]) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  style?: React.CSSProperties;
  maxWidth?: number;
}

export const MultiSelectWithLabel: React.FC<MultiSelectWithLabelProps> = (
  props
) => {
  const [isOpen, setIsOpen] = useState(false);
  const multiSelectRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const hasValue = props.values && props.values.length > 0;
  const selectedOptions = props.options.filter(
    (option) => props.values && props.values.includes(option.value)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        multiSelectRef.current &&
        !multiSelectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        props.onBlur?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props.onBlur]);

  const handleToggle = () => {
    if (props.disabled) return;

    if (!isOpen) {
      props.onFocus?.();
    }
    setIsOpen(!isOpen);
  };

  const handleOptionToggle = (optionValue: string) => {
    if (props.disabled) return;

    const currentValues = props.values || [];
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter((v) => v !== optionValue)
      : [...currentValues, optionValue];

    props.onChange?.(newValues);
  };
  const handleSelectAll = () => {
    if (props.disabled) return;

    const allValues = props.options.map((option) => option.value);
    props.onChange?.(allValues);
  };

  const handleClearAll = () => {
    if (props.disabled) return;

    props.onChange?.([]);
  };

  const renderSelectedValues = () => {
    if (!hasValue) {
      return <PlaceholderText>{props.placeholder}</PlaceholderText>;
    }

    const displayTags = selectedOptions.slice(
      0,
      props.maxDisplayTags ? props.maxDisplayTags : 2
    );
    const remainingCount =
      selectedOptions.length -
      (props.maxDisplayTags ? props.maxDisplayTags : 2);

    return (
      <SelectedContainer>
        {displayTags.map((option) => (
          <SelectedTag key={option.value}>{option.label}</SelectedTag>
        ))}
        {remainingCount > 0 && (
          <MoreIndicator>+{remainingCount} till</MoreIndicator>
        )}
      </SelectedContainer>
    );
  };

  return (
    <MultiSelectContainer maxWidth={props.maxWidth} ref={multiSelectRef} className="multi-select">
      <MultiSelectLabel required={props.required} disabled={props.disabled}>
        {props.label}
      </MultiSelectLabel>

      <MultiSelectInput
        hasError={props.hasError}
        fontSize={props.fontSize}
        isOpen={isOpen}
        disabled={props.disabled}
        onClick={handleToggle}
      >
        {renderSelectedValues()}
        {props.disabled ? (
          <EyeIcon>
            <Eye size={16} />
          </EyeIcon>
        ) : (
          <MultiSelectArrow isOpen={isOpen} />
        )}
      </MultiSelectInput>

      {!props.disabled && (
        <MultiSelectList isOpen={isOpen}>
          {props.showSelectAll && (
            <ActionButtons>
              <ActionButton onClick={handleSelectAll}>{t('General.ChooseAll')}</ActionButton>
              <ActionButton onClick={handleClearAll}>{t('General.ClearAll')}</ActionButton>
            </ActionButtons>
          )}

          {props.options.map((option) => {
            const isChecked =
              props.values && props.values.includes(option.value);

            return (
              <MultiSelectItem
                key={option.value}
                onClick={() => handleOptionToggle(option.value)}
              >
                <Checkbox checked={isChecked} />
                <OptionLabel>{option.label}</OptionLabel>
              </MultiSelectItem>
            );
          })}
        </MultiSelectList>
      )}
    </MultiSelectContainer>
  );
};
